/**
 * ThryvQualified shared helpers — exposed as ThryvQualified.Helpers.
 *
 * Pure-ish utility functions used by Events.js (and any future modules
 * that need them). No DOM event subscription, no analytics side effects —
 * just transforms and async lookups.
 */

const Helpers = new Object();

/**
 * Poll until window.qualified() is callable, then run callback.
 * Replaces the legacy helpers.execWhen('typeof qualified === "function"', ...)
 * pattern. Times out after ~5s if the SDK never loads.
 */
Helpers.waitForSdk = function (callback) {
  let attempts = 0;
  const maxAttempts = 25; // 5s @ 200ms

  const tick = setInterval(function () {
    if (typeof window.qualified === "function") {
      clearInterval(tick);
      callback();
    } else if (++attempts >= maxAttempts) {
      clearInterval(tick);
    }
  }, 200);
};

/** Split a full name string into first and last. */
Helpers.splitFullName = function (name) {
  if (!name) return { firstname: "", lastname: "" };
  const parts = name.trim().split(" ");
  return {
    firstname: parts[0] || "",
    lastname: parts.slice(1).join(" "),
  };
};

/** Truncate an ISO date string to minute granularity. */
Helpers.removeSecondsFromISODate = function (isoDate) {
  return new Date(isoDate).toISOString().slice(0, 16) + "Z";
};

/**
 * Walk a nested object into URLSearchParams using bracket-notation keys
 * (e.g. customer[firstName]=Jane). Matches jQuery $.param() output so the
 * Thryv ESB endpoint accepts the same payload shape.
 */
Helpers.toFormParams = function (obj) {
  const params = new URLSearchParams();

  const walk = function (value, key) {
    if (value === null || value === undefined) {
      params.append(key, "");
    } else if (typeof value !== "object") {
      params.append(key, String(value));
    } else if (Array.isArray(value)) {
      value.forEach((v, i) => walk(v, `${key}[${i}]`));
    } else {
      Object.keys(value).forEach((k) => {
        walk(value[k], key ? `${key}[${k}]` : k);
      });
    }
  };

  walk(obj, "");
  return params;
};

/** Build the Thryv ESB payload from a Qualified event data object. */
Helpers.buildEsbPayload = function (data) {
  const fv = data.field_values || {};
  const nameParts = (fv.name || "").split(" ");
  const hsLead = localStorage.thryv_lead_hubspot || "";

  return {
    meta: {
      extId: (data.bot && data.bot.id) || "",
      source: fv.source,
    },
    scheduledEvent: {
      transactionType: fv.transactiontype,
      id: fv.id,
      startDateTime: this.removeSecondsFromISODate(fv.startdatetime),
      endDateTime: this.removeSecondsFromISODate(fv.enddatetime),
    },
    customer: {
      hsContactId: fv.record_id && fv.record_id !== "" ? fv.record_id : hsLead + ".0",
      sfPersonId: fv.sfdc_record_id !== "undefined" ? fv.sfdc_record_id : "00Q5c00000yyLeTEAU",
      businessName: fv.company || "",
      firstName: nameParts[0] || "",
      lastName: nameParts.slice(-1)[0] || "",
      address: { countryCode: window.thryvData && window.thryvData.thryv_country_alpha2_code },
      phone: fv.phone_number_input_only,
      email: fv.email,
    },
    demoRepId: fv.demorepid,
  };
};

/** Look up HubSpot contact ID by email and persist to localStorage. */
Helpers.fetchHubspotContactId = function (email) {
  if (!email) return;

  return fetch(window.thryvData.ajax_url, {
    method: "POST",
    body: new URLSearchParams({ email, action: "getHubspotID" }),
  })
    .then((r) => r.json())
    .then((response) => {
      if (response && response.data && response.data !== "") {
        const info = JSON.parse(response.data);
        localStorage.setItem("thryv_lead_hubspot", info.id);
      }
      return response;
    })
    .catch(function () {
      // Non-critical lookup; swallow to avoid unhandled-rejection noise.
    });
};

export default Helpers;
